import Timer from "slotworks/utils/timer";
import * as TweenJS from "slotworks/utils/tween";

declare const __DEBUG__: boolean;

export default class Contract<T> {

    public static empty(): Contract<any> {
        return new Contract<any>((resolve) => resolve(null));
    }

    public static wrap(method?: Function): Contract<any> {
        return new Contract<any>((resolve) => {
            if (method) {
                method();
            }
            resolve(null);
        });
    }

    public static wrapTween(tween: TweenJS.Tween): Contract<any> {
        return new Contract<any>((resolve) => {

            let userOnComplete: (object?: any) => void;
            if (tween.getOnComplete()) {
                userOnComplete = tween.getOnComplete();
            }

            tween.onComplete(() => {
                if (userOnComplete) {
                    userOnComplete();
                }
                resolve(null);
            });

            tween.start();
        });
    }

    public static getTimeoutContract(duration: number): Contract<any> {
        return new Contract<number>((resolve) => {
            Timer.setTimeout(resolve, duration);
        });
    }

    public static getDelayedContract<T>(delay: number, callback: () => Contract<T>): Contract<T> {
        return new Contract((resolve) => {
            if (delay) {
                Timer.setTimeout(() => {
                    callback().then(resolve);
                }, delay);
            } else {
                callback().then(resolve);
            }
        });
    }

    private executor: (resolve: (value: T) => void) => (void | Contract<any>);

    private timeoutId: number;

    /**
     * A Contract is like a Promise however the results can be returned immediately (syncronously)
     * A Contract will ALWAYS be fulfilled
     * A Contract is only executed when "then" or "execute" is called (and never both)
     * If "then" or "execute" aren't called on a contract by the next "tick", execute is automatically called
     */
    constructor(executor: (resolve: (value: T) => void) => (void | Contract<any>)) {
        this.executor = executor;

        if (__DEBUG__) {
            const e = new Error();

            this.timeoutId = Timer.setTimeout(() => {
                throw new Error("Contract never executed " + e.stack);
            }, 1);
        }
    }

    public then(onfulfilled: (value: T) => void) {
        if (__DEBUG__) {
            Timer.clearTimeout(this.timeoutId);
        }

        if (this.executor) {
            let fulfilled = false;
            this.executor((value: T) => {
                if (!fulfilled) {
                    fulfilled = true;
                } else {
                    const e = new Error();
                    throw new Error("Contract resolved twice " + e.stack);
                }
                onfulfilled(value);
            });
            this.executor = null;
        }

        return this;
    }

    public execute() {
        if (__DEBUG__) {
            Timer.clearTimeout(this.timeoutId);
        }

        if (this.executor) {
            this.executor(() => null);
            this.executor = null;
        }
    }
}



// WEBPACK FOOTER //
// ./~/slotworks/utils/contract.ts